import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';

import { Typography } from '@universal-tennis/ui-shared';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';

import LoadingIndicator from '../LoadingIndicator';
import PlayersTable from '../Tables/PlayersTable';
import PlayerTab from './PlayersTab';
import AddPlayerModal from '../Modals/AddPlayerModal';
import BulkAddPlayersModal from '../Modals/BulkAddPlayersModal';
import { useStatsig } from '../../hooks/StatsigContextProvider';

import { groupPlayersByStatus } from '../../utils/teams';
import { showErrorPopup } from '../../redux/errorPopupSlice';
import { useGetPlayersQuery, useGetSessionTeamsQuery } from '../../api/leaguesApi';
import { TypographySizes, SharedUICategories } from '../../utils/constants';
import { playerTabPropTypes } from '../../utils/proptypes';

const PlayersTab = ({
    sessionId,
    onWithdrawPlayer,
    onMovePlayer,
    onCopyPlayer,
    onTogglePlayerStatus,
    isManaging = false,
    hasSessionEnded,
    isUpdating,
    displaySinglesAndDoublesRecord,
    sessionLocation,
    fallbackLocations,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { MEDIUM_MEDIUM } = TypographySizes;
    const { SECONDARY } = SharedUICategories;
    const [teamId, setTeamId] = useState('');
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
    const [showBulkAddModal, setShowBulkAddModal] = useState(false);
    const { statsig } = useStatsig();
    const canDirectAdd = isManaging && statsig?.featureGates?.organizerDirectAdd;

    const {
        data: teamPlayers,
        isLoading: isLoadingPlayers,
        error: playersError,
        isFetching: isFetchingPlayers,
    } = useGetPlayersQuery(teamId, { skip: !teamId });
    const { data: sessionTeams, isFetching: isLoadingSessionTeams, error: teamsError } = useGetSessionTeamsQuery(sessionId);

    const isLoading = isUpdating || isLoadingPlayers || isLoadingSessionTeams || isFetchingPlayers;

    useEffect(() => {
        if (teamsError) {
            dispatch(showErrorPopup(teamsError));
        }
        if (playersError) {
            dispatch(showErrorPopup(playersError));
        }
    }, [teamsError, playersError]);

    useEffect(() => {
        if (sessionTeams) {
            const teamSelections = sessionTeams?.teams?.map((team) => ({ id: team.id, description: team.name }));
            setTeams(teamSelections);
        }
    }, [sessionTeams]);

    useEffect(() => {
        if (teamPlayers) {
            setPlayers(groupPlayersByStatus(teamPlayers));
        }
    }, [teamPlayers]);

    const handleOnTeamChange = (event) => {
        setTeamId(event.target.value);
    };

    const renderContent = () => {
        const showSelectTeamMessage = !teamId && !players?.rostered?.length && !players?.substitutes?.length && !isLoading;
        const showNoPlayersMessage = teamId && !isLoading && !players?.rostered?.length && !players?.substitutes?.length;

        if (showSelectTeamMessage) {
            return (
                <Typography align="center" category={SECONDARY} size={MEDIUM_MEDIUM} color={theme.appColors.black}>
                    Select team from dropdown.
                </Typography>
            );
        }

        if (showNoPlayersMessage) {
            return (
                <Typography align="center" category={SECONDARY} size={MEDIUM_MEDIUM} color={theme.appColors.black}>
                    No players have registered for this team.
                </Typography>
            );
        }

        return (
            <>
                {displaySinglesAndDoublesRecord ? (
                    <PlayerTab teamId={teamId} />
                ) : (
                    <>
                        {players?.rostered?.length > 0 && (
                            <Box>
                                <Typography category={SECONDARY} size={MEDIUM_MEDIUM} color={theme.appColors.black}>
                                    Rostered
                                </Typography>
                                <PlayersTable
                                    hasSessionEnded={hasSessionEnded}
                                    isManaging={isManaging}
                                    teamPlayers={players.rostered}
                                    onWithdrawPlayer={onWithdrawPlayer}
                                    onMovePlayer={onMovePlayer}
                                    onCopyPlayer={onCopyPlayer}
                                    onTogglePlayerStatus={onTogglePlayerStatus}
                                />
                            </Box>
                        )}
                        {players?.substitutes?.length > 0 && (
                            <Box mt={5}>
                                <Typography category={SECONDARY} size={MEDIUM_MEDIUM} color={theme.appColors.black}>
                                    Substitutes
                                </Typography>
                                <PlayersTable
                                    hasSessionEnded={hasSessionEnded}
                                    isManaging={isManaging}
                                    teamPlayers={players.substitutes}
                                    onWithdrawPlayer={onWithdrawPlayer}
                                    onMovePlayer={onMovePlayer}
                                    onCopyPlayer={onCopyPlayer}
                                    onTogglePlayerStatus={onTogglePlayerStatus}
                                />
                            </Box>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <Box mt={4}>
            {!teams?.length && !isLoading ? (
                <Box py={4} textAlign="center">
                    <Typography category={SECONDARY} size={MEDIUM_MEDIUM} color={theme.appColors.black}>
                        No teams have been created for this session.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <FormControl sx={{ width: 300 }}>
                            <InputLabel id="team-select">Select Team</InputLabel>
                            <Select
                                value={teamId}
                                disabled={isLoading}
                                label="Select Team"
                                labelId="team-select"
                                onChange={handleOnTeamChange}
                            >
                                {teams?.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {canDirectAdd && teamId && (
                            <Tooltip title={hasSessionEnded ? 'This session has ended' : ''}>
                                <span>
                                    <Button
                                        variant="dark"
                                        disabled={hasSessionEnded}
                                        onClick={() => setShowAddPlayerModal(true)}
                                    >
                                        Add Player
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {canDirectAdd && teamId && (
                            <Tooltip title={hasSessionEnded ? 'This session has ended' : ''}>
                                <span>
                                    <Button
                                        variant="light"
                                        disabled={hasSessionEnded}
                                        onClick={() => setShowBulkAddModal(true)}
                                    >
                                        Add by Player ID
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                    {isLoading ? <LoadingIndicator /> : renderContent()}
                    {canDirectAdd && teamId && (
                        <AddPlayerModal
                            open={showAddPlayerModal}
                            onClose={() => setShowAddPlayerModal(false)}
                            teamId={teamId}
                            teamName={teams?.find((team) => team.id === teamId)?.description}
                            sessionId={sessionId}
                            sessionLocation={sessionLocation}
                            fallbackLocations={fallbackLocations}
                        />
                    )}
                    {canDirectAdd && teamId && (
                        <BulkAddPlayersModal
                            open={showBulkAddModal}
                            onClose={() => setShowBulkAddModal(false)}
                            sessionId={sessionId}
                            teams={teams}
                            defaultTeamId={teamId}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

PlayersTab.propTypes = {
    ...playerTabPropTypes,
};

export default PlayersTab;
